import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, LifeBuoy, Paperclip, X, FileIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

type Attachment = { path: string; name: string; size: number; type: string };

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;
const ALLOWED_TYPES = /^(image\/|application\/pdf|text\/|application\/(zip|msword|vnd\.openxmlformats))/;

async function uploadAttachments(files: File[], userId: string, ticketId: string): Promise<Attachment[]> {
  const uploads: Attachment[] = [];
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) throw new Error(`${file.name} exceeds 10MB`);
    if (!ALLOWED_TYPES.test(file.type)) throw new Error(`${file.name}: unsupported file type`);
    const ext = file.name.split(".").pop() ?? "bin";
    const path = `${userId}/${ticketId}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("ticket-attachments").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    if (error) throw error;
    uploads.push({ path, name: file.name, size: file.size, type: file.type });
  }
  return uploads;
}

export const Route = createFileRoute("/_authenticated/_account/help")({
  component: HelpCenter,
});

type Ticket = {
  id: string;
  user_id: string | null;
  subject: string;
  body: string;
  category: string | null;
  status: "open" | "pending" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  created_at: string;
  updated_at: string;
};

type Reply = {
  id: string;
  ticket_id: string;
  author_id: string | null;
  body: string;
  is_internal: boolean;
  created_at: string;
  attachments: Attachment[] | null;
};

const statusVariant: Record<Ticket["status"], "default" | "secondary" | "outline"> = {
  open: "default",
  pending: "secondary",
  resolved: "outline",
  closed: "outline",
};

const CATEGORIES = [
  "Web Development",
  "Mobile Apps",
  "AI Automation",
  "Digital Marketing",
  "UI / UX Design",
  "API & Integrations",
  "Cloud & Hosting",
  "Domains",
  "ERP Systems",
  "POS Systems",
  "Software & Licenses",
  "Billing & Payments",
  "Account",
  "Sales",
  "Technical Support",
  "Other",
] as const;

const newTicketSchema = z.object({
  subject: z.string().trim().min(3, "Subject must be at least 3 characters").max(200),
  category: z.enum(CATEGORIES),
  body: z.string().trim().min(10, "Please describe the issue (min 10 characters)").max(5000),
  priority: z.enum(["low", "normal", "high", "urgent"]),
});

function HelpCenter() {
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["my_support_tickets", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data as Ticket[];
    },
    enabled: !!user,
  });

  if (selectedId) {
    return <TicketView id={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Help Center</h1>
          <p className="text-sm text-muted-foreground">
            View your support tickets or open a new one. Our team typically replies within 24 hours.
          </p>
        </div>
        <NewTicketDialog />
      </div>

      <div className="rounded-lg border border-border bg-card">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : !tickets?.length ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <LifeBuoy className="h-10 w-10 text-muted-foreground" />
            <div className="text-sm font-medium">No tickets yet</div>
            <p className="text-sm text-muted-foreground">Open one when you need a hand.</p>
            <NewTicketDialog />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {tickets.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => setSelectedId(t.id)}
                  className="flex w-full items-center justify-between gap-4 p-4 text-left transition hover:bg-muted/40"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{t.subject}</div>
                    <div className="text-xs text-muted-foreground">
                      Updated {formatDistanceToNow(new Date(t.updated_at), { addSuffix: true })}
                    </div>
                  </div>
                  <Badge variant={statusVariant[t.status]} className="capitalize">
                    {t.status}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function NewTicketDialog() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>("Technical Support");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState<Ticket["priority"]>("normal");

  const create = useMutation({
    mutationFn: async () => {
      const parsed = newTicketSchema.parse({ subject, category, body, priority });
      if (!user) throw new Error("You must be signed in");
      const { error } = await supabase.from("support_tickets").insert({
        user_id: user.id,
        subject: parsed.subject,
        category: parsed.category,
        body: parsed.body,
        priority: parsed.priority,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Ticket submitted");
      setOpen(false);
      setSubject("");
      setCategory("Technical Support");
      setBody("");
      setPriority("normal");
      qc.invalidateQueries({ queryKey: ["my_support_tickets"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to submit"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" /> New ticket
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Open a new ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What can we help with?"
              maxLength={200}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as typeof category)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as Ticket["priority"])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="body">Message</Label>
            <Textarea
              id="body"
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Share as much detail as you can…"
              maxLength={5000}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? "Submitting…" : "Submit ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketView({ id, onBack }: { id: string; onBack: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const openAttachment = async (path: string) => {
    const { data, error } = await supabase.storage
      .from("ticket-attachments")
      .createSignedUrl(path, 60);
    if (error || !data) {
      toast.error("Could not open file");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const { data: ticket } = useQuery({
    queryKey: ["my_support_tickets", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as Ticket;
    },
  });

  const { data: replies } = useQuery({
    queryKey: ["ticket_replies", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ticket_replies")
        .select("*")
        .eq("ticket_id", id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as Reply[];
    },
  });

  const sendReply = useMutation({
    mutationFn: async () => {
      const text = reply.trim();
      if (text.length < 1 && files.length === 0) throw new Error("Reply cannot be empty");
      if (text.length > 5000) throw new Error("Reply is too long");
      if (!user) throw new Error("You must be signed in");
      const attachments = files.length ? await uploadAttachments(files, user.id, id) : [];
      const { error } = await supabase.from("ticket_replies").insert({
        ticket_id: id,
        author_id: user.id,
        body: text,
        is_internal: false,
        attachments,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setReply("");
      setFiles([]);
      qc.invalidateQueries({ queryKey: ["ticket_replies", id] });
      qc.invalidateQueries({ queryKey: ["my_support_tickets"] });
      toast.success("Reply sent");
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to send"),
  });

  if (!ticket) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  const isClosed = ticket.status === "closed" || ticket.status === "resolved";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <h1 className="text-xl font-semibold">{ticket.subject}</h1>
        <Badge variant={statusVariant[ticket.status]} className="capitalize">
          {ticket.status}
        </Badge>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-2 text-xs text-muted-foreground">
          Opened {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
        </div>
        <p className="whitespace-pre-wrap text-sm">{ticket.body}</p>
      </div>

      <div className="space-y-3">
        {replies?.map((r) => {
          const mine = r.author_id === user?.id;
          return (
            <div
              key={r.id}
              className={`rounded-lg border p-3 ${
                mine ? "border-primary/30 bg-primary/5" : "border-border bg-card"
              }`}
            >
              <div className="mb-1 text-xs text-muted-foreground">
                {mine ? "You" : "Support"} ·{" "}
                {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
              </div>
              {r.body && <p className="whitespace-pre-wrap text-sm">{r.body}</p>}
              {r.attachments && r.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.attachments.map((a) => (
                    <button
                      key={a.path}
                      type="button"
                      onClick={() => openAttachment(a.path)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted"
                    >
                      <FileIcon className="h-3 w-3" />
                      <span className="max-w-[200px] truncate">{a.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isClosed ? (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          This ticket is {ticket.status}. Open a new ticket if you need more help.
        </div>
      ) : (
        <div className="space-y-3 rounded-lg border border-border bg-card p-4">
          <Label>Add a reply</Label>
          <Textarea
            rows={4}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a message…"
            maxLength={5000}
          />
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs"
                >
                  <FileIcon className="h-3 w-3" />
                  <span className="max-w-[200px] truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Remove ${f.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <Label
              htmlFor="reply-attach"
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <Paperclip className="h-3.5 w-3.5" /> Attach files
            </Label>
            <input
              id="reply-attach"
              type="file"
              multiple
              accept="image/*,application/pdf,text/*,.doc,.docx,.zip"
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                const combined = [...files, ...picked].slice(0, MAX_FILES);
                if (files.length + picked.length > MAX_FILES) {
                  toast.error(`You can attach up to ${MAX_FILES} files`);
                }
                setFiles(combined);
                e.target.value = "";
              }}
            />
            <Button onClick={() => sendReply.mutate()} disabled={sendReply.isPending}>
              {sendReply.isPending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
